import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { logger } from '../utils/logger';

import {
  getProjects,
  // getProject, // Not used
  createProject,
  updateProject,
  deleteProject,
  testUrl,
  discoverSegments,
  previewScope,
  updateScope,
  cancelCrawlJob,
  setPageTier,
  clearPageTier,
  getProjectSchedule,
  updateProjectSchedule,
  pauseProject,
  resumeProject,
  type MonitoringProject
} from '../api/monitoring';
import type {
  ScopePreviewRequest,
  UpdateScopeRequest,
  ProjectSchedule
} from '../api/types';

interface ProjectsResponse {
  projects: MonitoringProject[];
  total: number;
  page: number;
  limit: number;
}

export function useMonitoringProjects(page = 1, limit: number | 'all' = 20) {
  const queryClient = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<MonitoringProject | null>(null);

  // Fetch projects with pagination
  const {
    data: projectsData,
    isLoading,
    error,
    refetch
  } = useQuery<ProjectsResponse>({
    queryKey: ['monitoring-projects', page, limit],
    queryFn: async () => {
      const projects = await getProjects();
      
      // Simulate pagination for now since backend doesn't support it yet
      const pageSize = limit === 'all' ? projects.length : limit;
      const startIndex = (page - 1) * pageSize;
      const paginatedProjects = limit === 'all' ? projects : projects.slice(startIndex, startIndex + pageSize);
      
      return {
        projects: paginatedProjects,
        total: projects.length,
        page,
        limit: pageSize
      };
    },
    refetchInterval: false, // Disable automatic polling - user can manually refresh if needed
    staleTime: 2 * 60 * 1000, // 2 minutes (increased from 1 minute)
    gcTime: 10 * 60 * 1000, // 10 minutes cache time
  });

  // Create project mutation
  const createProjectMutation = useMutation({
    mutationFn: async (projectData: {
      name: string;
      url?: string;
      webhook_url?: string;
      custom_headers?: Record<string, string>;
      scope_type?: import('../api/types').ScopeType;
      specific_urls?: string[];
      segment_filters?: import('../api/types').SegmentFilter[];
      max_urls?: number;
      crawl_frequency?: 'daily' | 'weekly';
      onboarding_intent_id?: string;
    }) => {
      return await createProject(projectData);
    },
    onSuccess: (newProject) => {
      // Update the cache directly instead of invalidating
      queryClient.setQueryData(['monitoring-projects', page, limit], (oldData: ProjectsResponse | undefined) => {
        if (!oldData) return oldData;
        if (oldData.projects.some((project) => project.id === newProject.id)) return oldData;
        return {
          ...oldData,
          projects: [newProject, ...oldData.projects],
          total: oldData.total + 1
        };
      });
      
      // Only invalidate other pages, not the current one
      queryClient.invalidateQueries({ 
        queryKey: ['monitoring-projects'], 
        exact: false,
        refetchType: 'none' // Don't refetch immediately
      });
    },
    onError: (error) => {
      logger.error('Failed to create project:', error);
    }
  });

  // Update project mutation
  const updateProjectMutation = useMutation({
    mutationFn: async ({ projectId, updates }: {
      projectId: string;
      updates: {
        name?: string;
        url?: string;
        webhook_url?: string | null;
        custom_headers?: Record<string, string>;
      }
    }) => {
      return await updateProject(projectId, updates);
    },
    onSuccess: (updatedProject) => {
      // Update the cache directly instead of invalidating
      queryClient.setQueryData(['monitoring-projects', page, limit], (oldData: ProjectsResponse | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          projects: oldData.projects.map(project => 
            project.id === updatedProject.id 
              ? updatedProject
              : project
          )
        };
      });
      
      // Update individual project cache if it exists
      queryClient.setQueryData(['project', updatedProject.id], updatedProject);
      
      // Update selected project if it's the one being updated
      if (selectedProject?.id === updatedProject.id) {
        setSelectedProject(updatedProject);
      }
    },
    onError: (error) => {
      logger.error('Failed to update project:', error);
    }
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      await deleteProject(projectId);
      return projectId;
    },
    onSuccess: (deletedProjectId) => {
      // Update the cache directly instead of invalidating
      queryClient.setQueryData(['monitoring-projects', page, limit], (oldData: ProjectsResponse | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          projects: oldData.projects.filter(project => project.id !== deletedProjectId),
          total: oldData.total - 1
        };
      });
      
      // Remove individual project cache
      queryClient.removeQueries({ queryKey: ['project', deletedProjectId] });
      
      // Clear selection if deleted project was selected
      if (selectedProject?.id === deletedProjectId) {
        setSelectedProject(null);
      }
    },
    onError: (error) => {
      logger.error('Failed to delete project:', error);
    }
  });

  // Test URL mutation
  const testUrlMutation = useMutation({
    mutationFn: async (url: string) => {
      return await testUrl(url);
    }
  });

  // 🎯 Scope-related mutations
  const discoverSegmentsMutation = useMutation({
    mutationFn: async (projectId: string) => {
      return await discoverSegments(projectId);
    }
  });

  const previewScopeMutation = useMutation({
    mutationFn: async ({ projectId, config }: { projectId: string; config: ScopePreviewRequest }) => {
      return await previewScope(projectId, config);
    }
  });

  const updateScopeMutation = useMutation({
    mutationFn: async ({ projectId, config }: { projectId: string; config: UpdateScopeRequest }) => {
      return await updateScope(projectId, config);
    },
    onSuccess: () => {
      // Invalidate projects cache to refetch with updated scope
      queryClient.invalidateQueries({ queryKey: ['monitoring-projects'] });
    },
    onError: (error) => {
      logger.error('Failed to update scope:', error);
    }
  });

  // 🛑 Cancel crawl job mutation
  const cancelCrawlJobMutation = useMutation({
    mutationFn: async (jobId: string) => {
      return await cancelCrawlJob(jobId);
    },
    onSuccess: () => {
      // Invalidate projects cache to reflect cancelled job
      queryClient.invalidateQueries({ queryKey: ['monitoring-projects'] });
    },
    onError: (error) => {
      logger.error('Failed to cancel crawl job:', error);
    }
  });

  // ⏸️ Pause project mutation — optimistic update
  const pauseProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      await pauseProject(projectId);
      return projectId;
    },
    onMutate: async (projectId) => {
      // Cancel any in-flight refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['monitoring-projects'] });

      // Snapshot previous value for rollback
      const previous = queryClient.getQueryData<ProjectsResponse>(['monitoring-projects', page, limit]);

      // Optimistically update the dashboard list cache
      queryClient.setQueryData(['monitoring-projects', page, limit], (oldData: ProjectsResponse | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          projects: oldData.projects.map(p =>
            p.id === projectId ? { ...p, status: 'paused' as const } : p
          )
        };
      });

      // Also update the individual project cache (used by MonitoringDetail)
      queryClient.setQueryData<MonitoringProject>(['project', projectId], (old) =>
        old ? { ...old, status: 'paused' as const } : old
      );

      return { previous };
    },
    onError: (_err, _projectId, context) => {
      // Revert optimistic update on failure
      if (context?.previous) {
        queryClient.setQueryData(['monitoring-projects', page, limit], context.previous);
      }
      queryClient.invalidateQueries({ queryKey: ['monitoring-projects'] });
    },
    onSettled: () => {
      // Always refetch after mutation settles to ensure server state
      queryClient.invalidateQueries({ queryKey: ['monitoring-projects'] });
    }
  });

  // ▶️ Resume project mutation — optimistic update
  const resumeProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      await resumeProject(projectId);
      return projectId;
    },
    onMutate: async (projectId) => {
      await queryClient.cancelQueries({ queryKey: ['monitoring-projects'] });

      const previous = queryClient.getQueryData<ProjectsResponse>(['monitoring-projects', page, limit]);

      queryClient.setQueryData(['monitoring-projects', page, limit], (oldData: ProjectsResponse | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          projects: oldData.projects.map(p =>
            p.id === projectId ? { ...p, status: 'active' as const } : p
          )
        };
      });

      queryClient.setQueryData<MonitoringProject>(['project', projectId], (old) =>
        old ? { ...old, status: 'active' as const } : old
      );

      return { previous };
    },
    onError: (_err, _projectId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['monitoring-projects', page, limit], context.previous);
      }
      queryClient.invalidateQueries({ queryKey: ['monitoring-projects'] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['monitoring-projects'] });
    }
  });

  // 🎯 Set page tier mutation
  const setPageTierMutation = useMutation({
    mutationFn: async ({ projectId, pageId, tier }: { projectId: string; pageId: string; tier: 'A' | 'B' | 'C' }) => {
      return await setPageTier(projectId, pageId, tier);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['snapshots'] });
    },
    onError: (error) => {
      logger.error('Failed to set page tier:', error);
    }
  });

  // 🎯 Clear page tier mutation
  const clearPageTierMutation = useMutation({
    mutationFn: async ({ projectId, pageId }: { projectId: string; pageId: string }) => {
      return await clearPageTier(projectId, pageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['snapshots'] });
    },
    onError: (error) => {
      logger.error('Failed to clear page tier:', error);
    }
  });

  // 📊 Get schedule mutation
  const getScheduleMutation = useMutation({
    mutationFn: async (projectId: string) => {
      return await getProjectSchedule(projectId);
    }
  });

  // 📊 Update schedule mutation (with tier-based support)
  const updateScheduleMutation = useMutation({
    mutationFn: async ({ projectId, schedule }: { projectId: string; schedule: Partial<ProjectSchedule> }) => {
      return await updateProjectSchedule(projectId, schedule);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['monitoring-projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['schedule-report', variables.projectId] });
    },
    onError: (error) => {
      logger.error('Failed to update schedule:', error);
    }
  });

  // Convenient wrapper functions
  const createProjectAction = useCallback((projectData: {
    name: string;
    url?: string;
    webhook_url?: string;
    custom_headers?: Record<string, string>;
    scope_type?: import('../api/types').ScopeType;
    specific_urls?: string[];
    segment_filters?: import('../api/types').SegmentFilter[];
    max_urls?: number;
  }) => {
    return createProjectMutation.mutateAsync(projectData);
  }, [createProjectMutation]);

  const updateProjectAction = useCallback((projectId: string, updates: {
    name?: string;
    url?: string;
    webhook_url?: string | null;
    custom_headers?: Record<string, string>;
  }) => {
    return updateProjectMutation.mutateAsync({ projectId, updates });
  }, [updateProjectMutation]);

  const deleteProjectAction = useCallback((projectId: string) => {
    return deleteProjectMutation.mutateAsync(projectId);
  }, [deleteProjectMutation]);

  const testUrlAction = useCallback((url: string) => {
    return testUrlMutation.mutateAsync(url);
  }, [testUrlMutation]);

  const refreshProjects = useCallback(() => {
    refetch();
  }, [refetch]);

  // 🎯 Scope action wrappers
  const discoverSegmentsAction = useCallback((projectId: string) => {
    return discoverSegmentsMutation.mutateAsync(projectId);
  }, [discoverSegmentsMutation]);

  const previewScopeAction = useCallback((projectId: string, config: ScopePreviewRequest) => {
    return previewScopeMutation.mutateAsync({ projectId, config });
  }, [previewScopeMutation]);

  const updateScopeAction = useCallback((projectId: string, config: UpdateScopeRequest) => {
    return updateScopeMutation.mutateAsync({ projectId, config });
  }, [updateScopeMutation]);

  // 🛑 Cancel crawl job action
  const cancelCrawlJobAction = useCallback((jobId: string) => {
    return cancelCrawlJobMutation.mutateAsync(jobId);
  }, [cancelCrawlJobMutation]);

  // ⏸️▶️ Pause / Resume actions
  const pauseProjectAction = useCallback((projectId: string) => {
    return pauseProjectMutation.mutateAsync(projectId);
  }, [pauseProjectMutation]);

  const resumeProjectAction = useCallback((projectId: string) => {
    return resumeProjectMutation.mutateAsync(projectId);
  }, [resumeProjectMutation]);

  // 🎯 Tier management actions
  const setPageTierAction = useCallback((projectId: string, pageId: string, tier: 'A' | 'B' | 'C') => {
    return setPageTierMutation.mutateAsync({ projectId, pageId, tier });
  }, [setPageTierMutation]);

  const clearPageTierAction = useCallback((projectId: string, pageId: string) => {
    return clearPageTierMutation.mutateAsync({ projectId, pageId });
  }, [clearPageTierMutation]);

  // 📊 Schedule actions (with tier-based support)
  const getScheduleAction = useCallback((projectId: string) => {
    return getScheduleMutation.mutateAsync(projectId);
  }, [getScheduleMutation]);

  const updateScheduleAction = useCallback((projectId: string, schedule: Partial<ProjectSchedule>) => {
    return updateScheduleMutation.mutateAsync({ projectId, schedule });
  }, [updateScheduleMutation]);

  // Helper functions for project filtering
  const getProjectsByStatus = useCallback((status: string) => {
    return projectsData?.projects.filter(project => project.status === status) || [];
  }, [projectsData]);

  const getActiveProjects = useCallback(() => {
    return projectsData?.projects.filter(project => project.status === 'active') || [];
  }, [projectsData]);

  const getProjectById = useCallback((projectId: string) => {
    return projectsData?.projects.find(project => project.id === projectId) || null;
  }, [projectsData]);

  const getProjectStats = useCallback(() => {
    const projects = projectsData?.projects || [];
    return {
      total: projects.length,
      active: projects.filter(p => p.status === 'active').length,
      paused: projects.filter(p => p.status === 'paused').length,
      error: projects.filter(p => p.status === 'error').length,
      totalChanges: projects.reduce((sum, p) => sum + p.changeCount, 0),
      totalAlerts: projects.reduce((sum, p) => sum + p.alertCount, 0)
    };
  }, [projectsData]);

  return {
    // Data
    projects: projectsData?.projects || [],
    total: projectsData?.total || 0,
    page: projectsData?.page || 1,
    limit: projectsData?.limit ?? (limit === 'all' ? 0 : limit),

    // State
    isLoading,
    error,
    selectedProject,
    setSelectedProject,

    // Actions
    createProject: createProjectAction,
    updateProject: updateProjectAction,
    deleteProject: deleteProjectAction,
    testUrl: testUrlAction,
    refreshProjects,

    // 🎯 Scope actions
    discoverSegments: discoverSegmentsAction,
    previewScope: previewScopeAction,
    updateScope: updateScopeAction,

    // 🛑 Job cancellation
    cancelCrawlJob: cancelCrawlJobAction,

    // ⏸️▶️ Pause / Resume
    pauseProject: pauseProjectAction,
    resumeProject: resumeProjectAction,

    // 🎯 Tier management actions
    setPageTier: setPageTierAction,
    clearPageTier: clearPageTierAction,

    // 📊 Schedule actions (with tier-based support)
    getSchedule: getScheduleAction,
    updateSchedule: updateScheduleAction,

    // Mutation states
    isCreating: createProjectMutation.isPending,
    isUpdating: updateProjectMutation.isPending,
    isDeleting: deleteProjectMutation.isPending,
    isTesting: testUrlMutation.isPending,
    isDiscoveringSegments: discoverSegmentsMutation.isPending,
    isPreviewingScope: previewScopeMutation.isPending,
    isUpdatingScope: updateScopeMutation.isPending,
    isCancellingJob: cancelCrawlJobMutation.isPending,
    isSettingTier: setPageTierMutation.isPending,
    isClearingTier: clearPageTierMutation.isPending,
    isGettingSchedule: getScheduleMutation.isPending,
    isUpdatingSchedule: updateScheduleMutation.isPending,

    // Helper functions
    getProjectsByStatus,
    getActiveProjects,
    getProjectById,
    getProjectStats
  };
}
